/**
 * Custom React hook that provides a function to update the commentId attribute
 * from NEW_COMMENT_ID to a new comment ID from the backend.
 *
 * @returns A function that accepts a `commentId` string and updates the selection's commentId attribute accordingly.
 */

import { useContext } from 'react';
import { history } from '@tiptap/pm/history';

import { DocumentContext } from '@modules/documents';

import findNodeWithSelection from '../helpers/findNodeWithSelection.helper';
import { SELECTION_MARK_NAME } from '../constants';

const useUpdateSelection = () => {
  const documentContext = useContext(DocumentContext);

  return (commentId: string) => {
    if (!documentContext) return;

    const { editor } = documentContext;
    const { state, view } = editor;

    const [foundNode, foundMark, posStart, posEnd] = findNodeWithSelection(editor);

    if (!foundNode) return;

    if (foundMark) {
      editor
        .chain()
        .focus()
        .setTextSelection({ from: posStart, to: posEnd })
        .updateAttributes(SELECTION_MARK_NAME, { commentId })
        .run();

      /*
        Alternative implementation:

        const tr: Transaction = state.tr;
        const markType: MarkType | undefined = schema.marks['selection-mark'];

        // Remove the existing mark
        tr.removeMark(posStart, posEnd, markType);

        // Add the updated mark
        tr.addMark(
          posStart,
          posEnd,
          markType.create({
            ...foundMark.attrs, commentId
          })
        );

        view.dispatch(tr);
      */
    } else {
      // create a transaction to update the node's attributes:
      const tr = state.tr.setNodeMarkup(
        posStart,
        undefined, // Keep the same node type
        { ...foundNode.attrs, commentId } // update attributes
      );
      view.dispatch(tr);
    }

    // clear history:
    editor.unregisterPlugin('history');
    editor.registerPlugin(history());
  };
};

export default useUpdateSelection;
